import React, { useEffect, useState, useRef, useCallback } from 'react';
import { getLivePositions, getRacePicks, getDrivers, getQuestions } from '../api';
import scorePicks from '../utilties/scoring';
import { scorePicksBreakdown } from '../utilties/scoring';

const POLL_INTERVAL = 15_000; // 15 seconds

function formatGap(val) {
    if (val == null) return '';
    if (typeof val === 'string') return val; // "+1 LAP"
    if (val === 0) return '';
    return typeof val === 'number' ? `+${val.toFixed(3)}` : String(val);
}

export default function LiveRace({ onLiveChange }) {
    const [live, setLive] = useState(null);       // response from backend
    const [picks, setPicks] = useState([]);
    const [driversCodeMap, setDriversCodeMap] = useState({});
    const [questions, setQuestions] = useState([]);
    const [error, setError] = useState('');
    const timerRef = useRef(null);
    const lastRaceId = useRef(null);

    const fetchPositions = useCallback(() => {
        getLivePositions()
            .then((data) => {
                setLive(data);
                setError('');
                if (data.isLive) {
                    onLiveChange && onLiveChange(true);
                    // Load picks/questions when race ID changes
                    if (data.raceId && data.raceId !== lastRaceId.current) {
                        lastRaceId.current = data.raceId;
                        Promise.all([
                            getRacePicks(data.raceId),
                            getDrivers(),
                            getQuestions(data.raceId),
                        ]).then(([pk, drv, qs]) => {
                            const codeMap = {};
                            (drv || []).forEach((d) => {
                                codeMap[d.id] = d.code || '';
                            });
                            setDriversCodeMap(codeMap);
                            setPicks(pk || []);
                            setQuestions(qs || []);
                        });
                    }
                } else {
                    onLiveChange && onLiveChange(false);
                }
            })
            .catch((e) => {
                console.error('LiveRace fetch error', e);
                setError('Unable to load live data');
                onLiveChange && onLiveChange(false);
            });
    }, [onLiveChange]);

    useEffect(() => {
        fetchPositions();
        timerRef.current = setInterval(fetchPositions, POLL_INTERVAL);
        return () => clearInterval(timerRef.current);
    }, [fetchPositions]);

    // Re-fetch picks periodically (people may submit picks late)
    useEffect(() => {
        if (!live?.isLive || !live?.raceId) return;
        const id = setInterval(() => {
            Promise.all([getRacePicks(live.raceId), getQuestions(live.raceId)])
                .then(([pk, qs]) => {
                    setPicks(pk || []);
                    setQuestions(qs || []);
                });
        }, 60_000); // every 60s
        return () => clearInterval(id);
    }, [live?.isLive, live?.raceId]);

    if (error) {
        return null; // silent fail — homepage falls back to normal view
    }

    if (!live || !live.isLive) {
        return null;
    }

    const positions = live.positions || [];

    // Build a results array indexed by position (0-based) with local driver IDs
    // for scoring against picks
    const liveResults = [];
    for (const p of positions) {
        if (p.driver_id && p.position >= 1) {
            liveResults[p.position - 1] = p.driver_id;
        }
    }
    const scorableResults = liveResults.slice(3, 10);

    // Sort picks by projected score descending
    const scoredPicks = picks.map((p) => {
        const userDnfId = p.dnf ? (p.dnf.id ?? p.dnf) : null;
        const breakdown = scorePicksBreakdown(liveResults, p.picks, [], userDnfId, questions, p.bonus_answers);
        const score = scorePicks(liveResults, p.picks, [], userDnfId, questions, p.bonus_answers);
        return { ...p, breakdown, score, userDnfId };
    }).sort((a, b) => b.score - a.score);

    return (
        <div className="card mt-3">
            <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <h5 className="card-title mb-0">
                        <span className="badge bg-danger me-2" style={{ fontSize: '0.7rem', verticalAlign: 'middle' }}>LIVE</span>
                        {live.raceName || 'Race'}
                    </h5>
                    {live.updatedAt && (
                        <span className="text-muted small">
                            Updated {new Date(live.updatedAt).toLocaleTimeString()}
                        </span>
                    )}
                </div>

                {/* Compact positions table */}
                <div className="table-responsive mb-3">
                    <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.85rem' }}>
                        <thead>
                            <tr>
                                <th style={{ width: 40 }}>Pos</th>
                                <th>Driver</th>
                                <th>Team</th>
                                <th>Gap</th>
                                <th>Int</th>
                            </tr>
                        </thead>
                        <tbody>
                            {positions.map((p) => (
                                <tr key={p.driver_number}>
                                    <td className="text-center fw-bold">{p.position}</td>
                                    <td>{p.code || p.name || `#${p.driver_number}`}</td>
                                    <td>{p.team || ''}</td>
                                    <td className="text-end">{p.position === 1 ? 'Leader' : formatGap(p.gap_to_leader)}</td>
                                    <td className="text-end">{p.position === 1 ? '' : formatGap(p.interval)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Projected picks scoring */}
                {scoredPicks.length > 0 && (
                    <>
                        <h6 className="mb-2">Projected Scores</h6>
                        <div className="table-responsive">
                            <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.85rem' }}>
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        {Array.from({ length: 7 }).map((_, i) => <th key={i}>#{i + 4}</th>)}
                                        <th>DNF</th>
                                        {questions.map((q, idx) => <th key={`q-${q.id}`}>Q{idx + 1}</th>)}
                                        <th>BNS</th>
                                        <th>Score</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scoredPicks.map((p) => (
                                        <tr key={p.user_id || p.username}>
                                            <td><strong>{p.display_name || p.username}</strong></td>
                                            {Array.from({ length: 7 }).map((_, i) => {
                                                const pickIndex = i + 3;
                                                const position = i + 4;
                                                const pick = p.picks[pickIndex];
                                                const pickId = pick ? (pick.id ?? pick) : null;
                                                const officialId = liveResults[pickIndex];
                                                const awarded = p.breakdown.positionPoints[position] || 0;
                                                let style = {};
                                                if (pickId && awarded > 0) {
                                                    style = awarded === 3 ? { backgroundColor: '#004911' } : { backgroundColor: '#3b3f00' };
                                                }
                                                const pickLabel = pick?.code || driversCodeMap[pickId] || '—';
                                                return <td key={i} style={style}>{pickLabel}</td>;
                                            })}
                                            <td>{p.userDnfId ? (p.dnf?.code || driversCodeMap[p.userDnfId] || '') : 'No DNF'}</td>
                                            {questions.map((q) => {
                                                const userAnswers = parseBonusAnswers(p.bonus_answers);
                                                const userAns = userAnswers[String(q.id)];
                                                const label = getAnswerLabel(q, userAns);
                                                let qStyle = {};
                                                if (q.correct_answer && userAns === q.correct_answer) {
                                                    qStyle = { backgroundColor: '#004911' };
                                                }
                                                return <td key={`q-${q.id}`} style={qStyle}>{label || '—'}</td>;
                                            })}
                                            <td>{p.breakdown.bonusPoints || 0}</td>
                                            <td className="fw-bold">{p.score}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="small text-muted mt-1">
                            Scores are projected based on live positions. DNF scoring excluded during live race.
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function parseBonusAnswers(str) {
    if (!str) return {};
    const map = {};
    str.split(',').forEach((pair) => {
        const [qId, ans] = pair.split(':');
        if (qId && ans) map[qId] = parseInt(ans);
    });
    return map;
}

function getAnswerLabel(q, answerNum) {
    if (!answerNum) return '';
    if (answerNum === 1) return q.option_1 || '';
    if (answerNum === 2) return q.option_2 || '';
    if (answerNum === 3) return q.option_3 || '';
    if (answerNum === 4) return q.option_4 || '';
    return '';
}
